import { useCustomerRepositoryUploadRequiredDocumentUpdate } from "@boundless/bl-api-client/customer/services"
import { CustomerRepositoryUploadRequiredDocumentUpdateBody as FormSchema } from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { FileUpload } from "@/components/form-elements/FileUploadGroup"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type {
  RepositoryDocument,
  RequiredDocumentDetails,
} from "@boundless/bl-api-client/customer/models"

function RequiredDocumentItem({
  requiredDocument,
  existingRepositoryDocument,
  companyUuid,
  onSuccess,
  className,
}: {
  requiredDocument: RequiredDocumentDetails
  existingRepositoryDocument?: RepositoryDocument
  companyUuid: string
  onSuccess: () => void
  className?: string
}) {
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      type: requiredDocument.typeId,
    },
  })

  const { mutate: uploadDocument, status: uploadStatus } =
    useCustomerRepositoryUploadRequiredDocumentUpdate({
      request: {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
      mutation: {
        onError: (error) => {
          console.error("Error uploading document:", error)
          applyErrorsToForm(
            form.setError,
            error as ErrorType<ErrorResponseType>,
          )
        },
        onSuccess: () => {
          onSuccess()
        },
      },
    })

  const requiredDocumentLabel =
    requiredDocument.name + (requiredDocument.isOptional ? "" : "*")

  return (
    <Form {...form}>
      <form>
        <FileUpload
          name={"file"}
          label={requiredDocumentLabel}
          initialFileName={existingRepositoryDocument?.filename}
          status={uploadStatus}
          className={className}
          onChange={form.handleSubmit((data) =>
            uploadDocument({ companyUuid, data }),
          )}
        />
      </form>
    </Form>
  )
}

export type RequiredDocumentsFormProps = {
  companyUuid: string
  requiredDocuments: ReadonlyArray<RequiredDocumentDetails>
  existingRepositoryDocuments: RepositoryDocument[]
  onSuccess: () => void
}

export function RequiredDocumentsForm({
  companyUuid,
  requiredDocuments,
  existingRepositoryDocuments,
  onSuccess = () => {},
}: RequiredDocumentsFormProps) {
  const requiredDocumentData = requiredDocuments.map((requiredDocument) => {
    const existingRepositoryDocument = existingRepositoryDocuments.find(
      (doc) => doc.type === requiredDocument.typeId,
    )
    return {
      requiredDocument,
      existingRepositoryDocument: existingRepositoryDocument,
    }
  })
  return (
    <div className="bg-accent flex flex-col gap-6 rounded-2xl p-6">
      {requiredDocumentData.map(
        ({ requiredDocument, existingRepositoryDocument }, i) => (
          <RequiredDocumentItem
            key={requiredDocument.typeId}
            requiredDocument={requiredDocument}
            existingRepositoryDocument={existingRepositoryDocument}
            companyUuid={companyUuid}
            onSuccess={onSuccess}
            // Add a top border divider to separate instances after the first
            className={i !== 0 ? "border-t border-gray-300 pt-6" : ""}
          />
        ),
      )}
    </div>
  )
}
